import RadioButton from '@bugbug/core/components/RadioButton';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChangeEventHandler } from 'react';

import type { SubscriptionBillingCycle } from '@bugbug/core/types/subscriptions';
import { SUBSCRIPTION_PERIOD } from '~/constants/subscription';
import {
  selectSubscription,
  selectSubscriptionSchedule,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import { useGetSubscriptionsPlansQuery } from '~/modules/subscriptions/subscriptions.api';

import { ActiveSubscriptionCard } from '../ActiveSubscriptionCard/ActiveSubscriptionCard';
import { CancelDowngradeCard } from '../CancelDowngradeCard/CancelDowngradeCard';
import { SUBSCRIPTION_PERIOD_DETAILS } from '../Subscription/Subscription.constants';
import { comparePlans } from '../Subscription/Subscription.helpers';
import { SectionTitle } from '../Subscription/Subscription.styled';
import { SubscriptionPlan } from '../SubscriptionPlan/SubscriptionPlan';

import * as S from './SubscriptionList.styled';

export const SubscriptionList = () => {
  const { t } = useTranslation();
  const { data: subscriptionsPlans = [] } = useGetSubscriptionsPlansQuery();

  const activeSubscription = useAppSelector(selectSubscription);
  const hasPaidPlan =
    activeSubscription && activeSubscription.type !== 'free' && !activeSubscription.isTrial;

  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule);
  const [billingCycle, setBillingCycle] = useState<SubscriptionBillingCycle>(
    hasPaidPlan ? activeSubscription.billingCycle : 'yearly',
  );
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleSubscriptionsPlans = useMemo(
    () =>
      subscriptionsPlans
        ?.filter(
          (subscription) =>
            subscription.billingCycle === billingCycle || subscription.plan === 'free',
        )
        .sort((a, b) => comparePlans(a, b)),
    [subscriptionsPlans, billingCycle],
  );

  const handleBillingCycleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setBillingCycle(event.target.value as SubscriptionBillingCycle);
  };

  const onChangePlan = () => {
    if (!headerRef.current) return;
    scrollTimeout.current = setTimeout(() => {
      headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  useEffect(
    () => () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    },
    [],
  );

  return (
    <div key={activeSubscription?.id ?? 'no-active-subscription'}>
      {hasPaidPlan && (
        <S.SubscriptionStatus>
          <ActiveSubscriptionCard onChangePlan={onChangePlan} />
          {subscriptionSchedule !== null && <CancelDowngradeCard />}
        </S.SubscriptionStatus>
      )}
      <S.Header ref={headerRef}>
        <S.TitleWrapper>
          <SectionTitle>
            {t('subscription.title', 'Choose your plan')}
            <S.PriceTaxInfo>
              {t('subscription.priceTaxInfo', '*Prices do not include VAT')}
            </S.PriceTaxInfo>
          </SectionTitle>
        </S.TitleWrapper>
        <S.RadioGroup>
          <RadioButton
            onChange={handleBillingCycleChange}
            value={SUBSCRIPTION_PERIOD.MONTHLY}
            checked={billingCycle === SUBSCRIPTION_PERIOD.MONTHLY}
          >
            {t('subscription.period.monthly.label', 'Monthly')}
          </RadioButton>
          <RadioButton
            onChange={handleBillingCycleChange}
            value={SUBSCRIPTION_PERIOD.YEARLY}
            checked={billingCycle === SUBSCRIPTION_PERIOD.YEARLY}
          >
            {t('subscription.period.yearly.label', 'Annually')}
            <S.Discount>
              {t('default.subscription.discount', {
                discount:
                  parseFloat(SUBSCRIPTION_PERIOD_DETAILS[SUBSCRIPTION_PERIOD.YEARLY].discount) *
                  100,
              })}
            </S.Discount>
          </RadioButton>
        </S.RadioGroup>
      </S.Header>
      <S.PlansGrid data-testid="PlansGrid">
        {visibleSubscriptionsPlans.map((subscription) => (
          <SubscriptionPlan
            key={subscription.type}
            plan={subscription}
            activeSubscription={activeSubscription}
          />
        ))}
      </S.PlansGrid>
    </div>
  );
};
